'use client'

import { Breakpoint as BreakPoint } from '@/types/types'
import { useGSAP } from '@gsap/react'
import { useSearchPropertyPopup } from '@/context/search-property-popup'
import gsap from 'gsap'
import Image from 'next/image'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { useRef, useState } from 'react'
import Button from '../button'

export default function AboveTheFold() {
  const containerRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<HTMLDivElement>(null)
  const austinRef = useRef<HTMLDivElement>(null)
  const heroTextContentAnimatedRef = useRef<HTMLDivElement>(null)
  const propertySearchSectionRef = useRef<HTMLDivElement>(null)
  const heroSectionRef = useRef<HTMLDivElement>(null)

  const smPropertySearchSectionRef = useRef<HTMLDivElement>(null)
  const smAustinRef = useRef<HTMLDivElement>(null)
  const smPropertySearchFormRef = useRef<HTMLDivElement>(null)
  const mainTimelineRef = useRef<gsap.core.Timeline | null>(null)
  const mainScrollTriggerRef = useRef<gsap.core.Tween['scrollTrigger'] | null>(
    null,
  )

  const [activeStep, setActiveStep] = useState(0)
  const [isDesktopPinned, setIsDesktopPinned] = useState(false)
  const [showScrollDownHint, setShowScrollDownHint] = useState(false)
  const sectionLabels = ['hero.end', 'property-search.end'] as const

  const { setValue } = useSearchPropertyPopup()

  /**
   * Computes pixel-based y translations for section 2 animations.
   * Uses offsetTop/offsetHeight which are NOT affected by CSS transforms,
   * so values remain correct even when called mid-animation.
   */
  function getSection2Positions() {
    const vh = window.innerHeight

    const austinEl = austinRef.current!
    const searchEl = propertySearchSectionRef.current!

    // Scale nudge and gap proportionally to viewport height (reference: 900px)
    const vhScale = vh / 900

    // Austin: nudge down slightly so it doesn't sit too high
    const austinNudge = 150 * vhScale
    const austinOutY = -austinEl.offsetTop + austinNudge

    // Search: starts fully below the viewport
    const searchInitY = vh - searchEl.offsetTop + searchEl.offsetHeight

    // Search: ends below austin with proportional gap
    const gap = 200 * vhScale
    const searchEndY = austinEl.offsetHeight + gap - searchEl.offsetTop

    return { austinOutY, searchInitY, searchEndY }
  }

  const textContentParentRef = useRef<HTMLDivElement | null>(null)

  function smoothScrollTo(position: number) {
    if (!Number.isFinite(position)) {
      return
    }

    gsap.to(window, {
      scrollTo: { y: position, autoKill: false },
      duration: 0.35,
      overwrite: 'auto',
      ease: 'none',
    })
  }

  function getStepScrollY(step: number) {
    const tl = mainTimelineRef.current
    const st = mainScrollTriggerRef.current
    if (!tl || !st) return null

    const label = sectionLabels[step]
    let targetY = st.start

    if (typeof st.labelToScroll === 'function') {
      targetY = st.labelToScroll(label)
    } else {
      const totalDuration = tl.totalDuration() || 1
      const labelTime = tl.labels[label] ?? 0
      const progress = Math.max(0, Math.min(labelTime / totalDuration, 1))
      targetY = st.start + (st.end - st.start) * progress
    }

    return gsap.utils.clamp(st.start, st.end, targetY)
  }

  function goToSection(step: number) {
    const targetStep = Math.max(0, Math.min(step, sectionLabels.length - 1))
    setActiveStep(targetStep)

    const desktopScrollY = getStepScrollY(targetStep)
    if (
      desktopScrollY !== null &&
      isDesktopPinned &&
      mainScrollTriggerRef.current
    ) {
      smoothScrollTo(desktopScrollY)
      return
    }

    const targetEl =
      targetStep === 0
        ? heroSectionRef.current
        : smPropertySearchSectionRef.current
    if (!targetEl) return

    const targetTop = targetEl.getBoundingClientRect().top + window.scrollY
    smoothScrollTo(targetTop)
  }

  function handleSectionNavigationNext() {
    goToSection(activeStep + 1)
  }

  function handleSectionNavigationPrev() {
    goToSection(activeStep - 1)
  }

  function animateForTabletOrDesktop({
    tl,
    section2Start,
    section2Duration,
  }: {
    tl: gsap.core.Timeline
    section2Start: number
    section2Duration: number
  }) {
    // fade out hero text content
    tl.to(
      textContentParentRef.current,
      {
        y: -window.innerHeight,
        autoAlpha: 0,
        duration: 1500,
        ease: 'power1.out',
      },
      section2Start,
    )

    // Austin moves to top of parent container
    tl.to(
      austinRef.current,
      {
        y: () => getSection2Positions().austinOutY,
        duration: 1000,
        ease: 'power2.inOut',
      },
      section2Start,
    )

    // Property search section slides up from bottom
    gsap.set(propertySearchSectionRef.current, {
      y: () => getSection2Positions().searchInitY,
    })

    tl.to(
      propertySearchSectionRef.current,
      {
        y: () => getSection2Positions().searchEndY,
        duration: section2Duration,
        ease: 'power2.inOut',
      },
      section2Start,
    )
  }

  function animateForMobile() {
    // Austin slides up
    const duration = 1.2

    gsap.fromTo(
      smAustinRef.current,
      {
        yPercent: 100,
      },
      {
        yPercent: 0,
        duration,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: smPropertySearchSectionRef.current,
          start: 'top bottom',
          toggleActions: 'play none none reset',
          invalidateOnRefresh: true,
        },
      },
    )

    gsap.fromTo(
      smPropertySearchFormRef.current,
      {
        yPercent: 100,
      },
      {
        yPercent: 0,
        delay: 0.3,
        duration,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: smPropertySearchSectionRef.current,
          start: 'top bottom',
          toggleActions: 'play none none reset',
          invalidateOnRefresh: true,
        },
      },
    )
  }

  useGSAP(
    () => {
      // property search section timings
      const section2Start = 0
      const section2Duration = 2500

      function createTimeline(breakPoint: BreakPoint) {
        const shouldPin = breakPoint !== 'sm'
        setIsDesktopPinned(shouldPin)

        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: containerRef.current,
            start: 'top top',
            end: `+=${4000}`,
            scrub: shouldPin,
            pin: shouldPin,
            invalidateOnRefresh: true,
            onUpdate: (self) => {
              if (shouldPin && mainTimelineRef.current) {
                const currentTl = mainTimelineRef.current
                const currentTime = self.progress * currentTl.totalDuration()

                const closestStep = sectionLabels.reduce(
                  (acc, label, index) => {
                    const labelTime = currentTl.labels[label] ?? 0
                    const distance = Math.abs(labelTime - currentTime)

                    if (distance < acc.distance) {
                      return { index, distance }
                    }

                    return acc
                  },
                  { index: 0, distance: Number.POSITIVE_INFINITY },
                )

                setActiveStep(closestStep.index)
                setShowScrollDownHint(
                  closestStep.index === sectionLabels.length - 1 &&
                    self.progress >= 0.9,
                )
              } else {
                setShowScrollDownHint(false)
              }
            },
          },
          defaults: {
            ease: 'none',
          },
        })

        tl.addLabel('hero.end', 0)

        mainScrollTriggerRef.current = tl.scrollTrigger ?? null
        mainTimelineRef.current = tl

        if (breakPoint === 'sm') {
          animateForMobile()
        } else {
          animateForTabletOrDesktop({
            tl,
            section2Start,
            section2Duration,
          })

          tl.addLabel('property-search.end', tl.totalDuration())
        }

        return () => {
          setShowScrollDownHint(false)
          mainTimelineRef.current = null
          mainScrollTriggerRef.current = null
          tl.kill()
        }
      }

      const mm = gsap.matchMedia()

      mm.add('(max-width: 767px)', () => {
        return createTimeline('sm')
      })

      mm.add('(min-width: 768px) and (max-width: 1023px)', () => {
        return createTimeline('md')
      })

      mm.add('(min-width: 1024px) and (max-width: 1279px)', () => {
        return createTimeline('lg')
      })

      mm.add('(min-width: 1280px)', () => {
        return createTimeline('xl')
      })

      return () => {
        mm.revert()
      }
    },
    { scope: containerRef },
  )

  return (
    <div
      ref={containerRef}
      className="relative w-svw min-h-svh bg-[#E2DBD8] overflow-x-hidden md:overflow-hidden"
    >
      {/* hero */}
      <section
        ref={heroSectionRef}
        className="w-dvw h-dvh relative flex flex-col justify-center xl:justify-between gap-y-6 py-6 md:py-0 px-4 md:px-6 bg-[#E2DBD8]"
      >
        <div
          className="flex flex-col gap-y-6 h-fit xl:pt-[4rem]"
          ref={textContentParentRef}
        >
          <div
            ref={logoRef}
            className="relative w-[85%] h-[2rem] md:h-[3rem] xl:h-[4rem] mx-auto mb-4 xl:mb-6"
          >
            <Image
              src="/images/pinnacle-logo.png"
              alt="logo"
              fill
              className="object-contain"
              priority
            />
          </div>

          <div
            ref={heroTextContentAnimatedRef}
            className="w-full xl:max-w-[1240px] mx-auto flex flex-col items-center justify-center gap-6 md:gap-8 text-[#0E0E10] px-4 md:px-0"
          >
            <p className="font-bold text-[12px] tracking-[8px] text-[#0E0E10] uppercase text-center">
              The peak of property tax expertise
            </p>
            <h1 className="text-3xl md:text-5xl xl:text-[82px] max-w-[1240px] text-center uppercase">
              Lower Your Property Taxes with Confidence.
            </h1>
            <p className="max-w-[1020px] text-size-[1.5rem] text-center">
              Professional property tax protest services for property owners
              seeking experienced, contingency-based representation to help
              protect against overassessment and unnecessary tax burdens.
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-3 md:gap-x-6">
              <Button text="File My Protest" onClick={() => setValue(true)} />
              <Button
                variant="outlined"
                text="See How Results-Based Fees Work"
                onClick={() => setValue(true)}
              />
            </div>
          </div>
        </div>

        <div
          ref={austinRef}
          className="will-change-transform relative w-full h-[10rem] md:h-[17rem] xl:h-[20rem] 2xl:h-[25rem]"
        >
          <Image
            src={'/images/austin1.png'}
            alt="austin"
            fill
            className="object-fill"
            priority
          />
        </div>

        <section
          ref={propertySearchSectionRef}
          className="will-change-transform w-full lg:h-[325px] px-6 md:px-20 lg:px-[150px] absolute left-0 right-0 hidden md:grid grid-cols-12 gap-x-4 lg:gap-x-20 items-center justify-center cursor-default xl:justify-between"
        >
          <div className="col-span-12 xl:col-span-8">
            <div className="flex flex-col gap-y-4 items-center xl:items-start">
              <p className="font-bold text-[12px]  tracking-[8px] text-[#0E0E10] uppercase">
                Property Search
              </p>
              <h1 className="uppercase text-[30px] md:text-[50px] text-center xl:text-left">
                Start with Your Property.
              </h1>
              <div className="w-full text-center xl:text-left">
                <Button
                  text="Search Property"
                  onClick={() => setValue(true)}
                  className="w-3/4 xl:w-fit px-"
                />
              </div>
              <span className="text-[12px] text-center xl:text-left">
                Secure. No upfront payment.
              </span>
            </div>
          </div>
          <div className="hidden md:col-span-6 xl:col-span-4 relative w-full h-full xl:flex items-center justify-center">
            <Image
              src="/images/texas.png"
              alt="texas"
              fill
              className="object-contain"
              priority
            />
          </div>
        </section>
      </section>

      {/* mobile - property search section */}
      <section
        ref={smPropertySearchSectionRef}
        className="w-full min-h-svh overflow-hidden md:hidden flex flex-col items-center justify-center gap-y-6 px-4"
      >
        <div
          ref={smAustinRef}
          className="relative will-change-transform w-full h-[14rem] md:h-[17rem] xl:h-[20rem] 2xl:h-[25rem]"
        >
          <Image
            src="/images/texas.png"
            alt="texas"
            fill
            className="object-contain"
            priority
          />
        </div>

        <div
          ref={smPropertySearchFormRef}
          className="will-change-transform flex flex-col gap-y-4 items-center xl:items-start"
        >
          <p className="font-bold text-[12px] tracking-[8px] text-[#0E0E10] uppercase">
            Property Search
          </p>
          <h1 className="uppercase text-[30px] md:text-[50px] text-center xl:text-left">
            Start with Your Property.
          </h1>
          <div className="w-full text-center xl:text-left">
            <Button
              text="Search Property"
              onClick={() => setValue(true)}
              className="w-3/4 xl:w-fit px-"
            />
          </div>
          <span className="text-[14px] text-center xl:text-left">
            Secure. No upfront payment.
          </span>
        </div>
      </section>
      {/* end mobile - property search section */}

      <div className="fixed right-4 bottom-1/2 z-40 hidden md:flex flex-col gap-2">
        <button
          type="button"
          aria-label="Go to previous section"
          onClick={() => handleSectionNavigationPrev()}
          disabled={activeStep === 0}
          className="h-11 w-11 rounded-full border border-[#877C77] bg-[#E2DBD8] text-[#0E0E10] shadow-sm flex items-center justify-center transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
        >
          <ChevronUp size={20} />
        </button>
        <button
          type="button"
          aria-label="Go to next section"
          onClick={() => handleSectionNavigationNext()}
          disabled={activeStep === 1}
          className="h-11 w-11 rounded-full border border-[#877C77] bg-[#E2DBD8] text-[#0E0E10] shadow-sm flex items-center justify-center transition-all disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
        >
          <ChevronDown size={20} />
        </button>
      </div>

      <div
        className={`hidden md:flex fixed left-1/2 bottom-6 z-40 -translate-x-1/2 items-center gap-3 rounded-full border-2 border-[#0E0E10] bg-[#E2DBD8] px-5 py-3 text-[13px] font-bold tracking-[2.5px] uppercase text-[#0E0E10] shadow-[0_0_0_3px_rgba(226,219,216,0.9),0_10px_30px_rgba(14,14,16,0.3)] transition-all duration-300 ${showScrollDownHint ? 'opacity-100 translate-y-0 scale-100 animate-pulse' : 'opacity-0 translate-y-2 scale-95 pointer-events-none'}`}
        aria-hidden={!showScrollDownHint}
      >
        <span>Scroll Down To Continue</span>
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#0E0E10] text-[#E2DBD8] animate-bounce">
          <ChevronDown size={14} />
        </span>
      </div>
    </div>
  )
}
